import React, { useState } from "react";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Box, Chip } from "@mui/material";
import "moment/locale/en-gb";
import { useLocation, useNavigate } from "react-router";
import { useMemo } from "react";
import { getEventColour, getEventIcon } from "../utils/data"; // Importing demoEvents from data.js
import CustomDailyAgenda from "./CustomDailyAgenda";

moment.locale("en-gb");

const localizer = momentLocalizer(moment);

const eventStyleGetter = (event, start, end, isSelected) => {
  let backgroundColor = getEventColour(event.eventType);

  return {
    style: {
      backgroundColor: backgroundColor,
      borderRadius: "4px",
      color: "white",
      border: "none",
      display: "block",
    },
  };
};

function EventsCalendar({
  selectedDate,
  activeFilters,
  handleFilterToggle,
  filteredEvents,
  onDateChange,
}) {
  const data = JSON.parse(localStorage.getItem("eventsData"));

  const navigate = useNavigate();
  const location = useLocation();
  const initialView = location.state?.targetView || "agenda";
  const [currentView, setCurrentView] = useState(initialView);
  const parsedDate = new Date(selectedDate);

  const uniqueEventTypes = useMemo(() => {
    if (!data || data.length === 0) return ["All"];
    return [
      "All",
      ...new Set(
        data
          .map((event) => event.eventType)
          .filter(Boolean)
          .sort(),
      ),
    ];
  }, [data]);

  return (
    <Box
      sx={{
        height: "80vh", // Ensure the calendar has a defined height so it renders properly
      }}
    >
      {/* Filter Bar */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          margin: "0 auto",
          mb: 2,
          pb: 1,
          maxWidth: 700,
          overflowX: "auto",
          "@media (max-width: 900px)": {
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          },
        }}
      >
        {uniqueEventTypes.map((type) => {
          const isActive =
            type === "All"
              ? activeFilters.length === 0
              : activeFilters.includes(type);
          const icon = getEventIcon(type);
          const colour = getEventColour(type);
          return (
            <Chip
              key={type}
              label={type}
              onClick={() => handleFilterToggle(type)}
              icon={icon}
              sx={{
                fontWeight: "bold",
                backgroundColor: isActive ? "#fff" : colour,
                border: isActive ? `2px solid ${colour}` : "none",
                color: isActive ? colour : "#fff",
                "& .MuiChip-icon": {
                  color: isActive ? colour : "#fff",
                },
              }}
            />
          );
        })}
      </Box>

      {currentView === "agenda" ? (
        <CustomDailyAgenda
          date={selectedDate}
          events={filteredEvents} // <-- Updated!
          onBack={() => setCurrentView("month")}
        />
      ) : (
        <Calendar
          localizer={localizer}
          events={filteredEvents}
          startAccessor="start"
          endAccessor="end"
          view={currentView}
          onView={(newView) => setCurrentView(newView)}
          date={parsedDate}
          onNavigate={(newDate) => onDateChange(newDate)}
          views={["month", "week", "day"]} // The views available in the toolbar
          eventPropGetter={eventStyleGetter}
          onSelectEvent={(event) => navigate(`/event/${event.eventID}`)} // Navigate to event details on click
          components={{
            toolbar: CustomToolbar, // Use our custom toolbar
          }}
        />
      )}
    </Box>
  );
}

function CustomToolbar({ label, view, onNavigate }) {
  // Decide button text based on current view
  const getNextLabel = () => {
    switch (view) {
      case "month":
        return "Next Month";
      case "week":
        return "Next Week";
      case "day":
        return "Next Day";
      case "agenda":
        return "Next Agenda";
      default:
        return "Next";
    }
  };

  const getPrevLabel = () => {
    switch (view) {
      case "month":
        return "Prev Month";
      case "week":
        return "Prev Week";
      case "day":
        return "Prev Day";
      case "agenda":
        return "Prev Agenda";
      default:
        return "Previous";
    }
  };

  return (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group">
        <button onClick={() => onNavigate("PREV")}>{getPrevLabel()}</button>
        <button onClick={() => onNavigate("TODAY")}>Today</button>
        <button onClick={() => onNavigate("NEXT")}>{getNextLabel()}</button>
      </span>
      <span className="rbc-toolbar-label">{label}</span>
    </div>
  );
}

export default EventsCalendar;
